import { GableChatMessage } from '../../../services/gable/gable-drawer.service';
import { LocalService } from '../../../services/storage/local.service';

/**
 * Feedback campaign: for 30 days Gable asks for a thumbs rating on more of its
 * answers than the permanent SD-10800 triggers (a decline, the reply after a
 * confirmed write). After this date only those triggers remain.
 */
export const FEEDBACK_CAMPAIGN_ENDS_AT = Date.parse('2026-11-20T00:00:00Z');

/** Ask on the 1st, 4th and 7th substantive answer of a conversation, never more. */
const ASK_EVERY = 3;
const MAX_ASKS_PER_CONVERSATION = 3;
/** An answer without tool calls counts as substantive from this length; shorter ones are chit-chat. */
const SUBSTANTIVE_CHARS = 280;
/** A vote on either of the two answers before this one means the reader already told us. */
const RECENT_VOTE_WINDOW = 2;

/** Asks ignored in a row before the campaign stops asking this person for a while. */
const IGNORED_LIMIT = 3;
const PAUSE_MS = 7 * 24 * 60 * 60 * 1000;

export function isSubstantiveAnswer(msg: GableChatMessage): boolean {
    const usedTools = (msg.toolsUsed?.length ?? 0) > 0
        || (msg.toolCards?.length ?? 0) > 0
        || (msg.thoughts ?? []).some((t) => (t.kind ?? 'tool') === 'tool');
    return usedTools || (msg.content ?? '').trim().length >= SUBSTANTIVE_CHARS;
}

/** What the cadence needs about each answer, worked out once per thread change rather than per message per frame. */
interface ThreadShape {
    latest: number;
    /** Substantive answers before each index. */
    ordinalBefore: number[];
    /** The assistant answers just before each index, nearest last. */
    recentBefore: number[][];
}

let shapeCache: { messages: ReadonlyArray<GableChatMessage>; key: string; shape: ThreadShape } | null = null;

/**
 * Changes whenever anything the cadence reads can change. Answers above the
 * last one are finished; only the last can still be streaming in place.
 */
export function threadKey(messages: ReadonlyArray<GableChatMessage>): string {
    const last = messages[messages.length - 1];
    return [
        messages.length,
        last?.streamState ?? '',
        last?.content?.length ?? 0,
        last?.toolCards?.length ?? 0,
        last?.toolsUsed?.length ?? 0,
        last?.thoughts?.length ?? 0,
    ].join('|');
}

function threadShape(messages: ReadonlyArray<GableChatMessage>): ThreadShape {
    const key = threadKey(messages);
    if (shapeCache?.messages === messages && shapeCache.key === key) {
        return shapeCache.shape;
    }
    const ordinalBefore: number[] = [];
    const recentBefore: number[][] = [];
    const recent: number[] = [];
    let ordinal = 0;
    let latest = -1;
    messages.forEach((m, i) => {
        ordinalBefore.push(ordinal);
        recentBefore.push(recent.slice(-RECENT_VOTE_WINDOW));
        if (m.role === 'assistant') {
            latest = i;
            recent.push(i);
            if (isSubstantiveAnswer(m)) {
                ordinal += 1;
            }
        }
    });
    const shape = { latest, ordinalBefore, recentBefore };
    shapeCache = { messages, key, shape };
    return shape;
}

/**
 * Whether the campaign asks for a rating on `messages[index]`. Pure: the same
 * thread always gives the same answer, so a template can bind to it.
 */
export function campaignAsksFor(
    messages: ReadonlyArray<GableChatMessage>,
    index: number,
    hasVote: (msg: GableChatMessage) => boolean,
): boolean {
    const msg = messages[index];
    if (!msg || msg.role !== 'assistant' || !isSubstantiveAnswer(msg)) {
        return false;
    }
    const shape = threadShape(messages);
    if (index !== shape.latest || shape.recentBefore[index].some((i) => hasVote(messages[i]))) {
        return false;
    }
    const ordinal = shape.ordinalBefore[index];
    return ordinal % ASK_EVERY === 0 && ordinal / ASK_EVERY < MAX_ASKS_PER_CONVERSATION;
}

interface AskMemoryState {
    ignored: number;
    pausedUntil: number;
}

/**
 * Per-person memory of how the asks land, so someone who keeps scrolling past
 * them stops seeing them. Lives in localStorage keyed by user id; a blocked or
 * empty storage just means the campaign behaves as if this is a first visit.
 */
export class GableFeedbackAskMemory {
    private _state: AskMemoryState;

    constructor(private readonly _key: string | null) {
        this._state = this._read();
    }

    isPaused(now: number): boolean {
        return now < this._state.pausedUntil;
    }

    noteIgnored(now: number): void {
        const ignored = this._state.ignored + 1;
        this._write(ignored >= IGNORED_LIMIT
            ? { ignored: 0, pausedUntil: now + PAUSE_MS }
            : { ...this._state, ignored });
    }

    noteVoted(): void {
        if (this._state.ignored !== 0) {
            this._write({ ...this._state, ignored: 0 });
        }
    }

    private _read(): AskMemoryState {
        const empty = { ignored: 0, pausedUntil: 0 };
        if (!this._key) {
            return empty;
        }
        try {
            const parsed = JSON.parse(LocalService.get(this._key) ?? 'null');
            return {
                ignored: Number(parsed?.ignored) || 0,
                pausedUntil: Number(parsed?.pausedUntil) || 0,
            };
        } catch {
            return empty;
        }
    }

    private _write(state: AskMemoryState): void {
        this._state = state;
        if (!this._key) {
            return;
        }
        try {
            LocalService.set(this._key, JSON.stringify(state));
        } catch {
            // Storage blocked: the in-memory state still holds for this session.
        }
    }
}
